import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, FlaskConical, Eye, Trash2, Milk, Factory } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { raw_milk: "Leche cruda", cream: "Crema", curd: "Cuajada" };
const TYPE_BADGE = { raw_milk: "bg-blue-100 text-blue-800", cream: "bg-amber-100 text-amber-800", curd: "bg-emerald-100 text-emerald-800" };

const empty = {
  sample_type: "raw_milk", reception_id: "", cream_folio: "", curd_folio: "",
  ph: "", acidity: "", fat_pct: "", protein_pct: "", lactose_pct: "",
  density: "", temperature_c: "", crioscopic_h: "", sng_pct: "", total_solids_pct: "", humidity_pct: "",
  alcohol_test: "", neutralizers: "", chlorinates: "", antibiotic: "",
  color: "", smell: "", taste: "", aspect: "",
  photo_url: "", notes: "",
};

export default function Laboratorio() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [productions, setProductions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [phInfo, setPhInfo] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const [l, r, p] = await Promise.all([api.get("/lab-samples"), api.get("/receptions"), api.get("/productions")]);
    setItems(l.data);
    setReceptions(r.data);
    setProductions(p.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!form.ph || form.sample_type !== "raw_milk") { setPhInfo(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/conversion/ph-to-acidity?ph=${parseFloat(form.ph)}`);
        setPhInfo(data);
        setForm((f) => ({ ...f, acidity: data.acidity.toString() }));
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [form.ph, form.sample_type]);

  const submit = async (e) => {
    e.preventDefault();
    const t = form.sample_type;
    if (t === "raw_milk" && !form.reception_id) return toast.error("Selecciona el lote de leche");
    if (t === "cream" && !form.cream_folio) return toast.error("Selecciona el lote de crema");
    if (t === "curd" && !form.curd_folio) return toast.error("Selecciona el lote de cuajada");
    try {
      const payload = { ...form };
      ["ph", "acidity", "fat_pct", "protein_pct", "lactose_pct", "density", "temperature_c", "crioscopic_h", "sng_pct", "total_solids_pct", "humidity_pct"].forEach((k) => {
        payload[k] = payload[k] !== "" ? parseFloat(payload[k]) : null;
      });
      await api.post("/lab-samples", payload);
      toast.success("Muestra registrada");
      setForm(empty);
      setPhInfo(null);
      setOpen(false);
      load();
    } catch { toast.error("Error al guardar"); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try { await api.delete(`/lab-samples/${toDelete.id}`); toast.success("Muestra eliminada"); setToDelete(null); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const isMilk = form.sample_type === "raw_milk";
  const isCream = form.sample_type === "cream";
  const isCurd = form.sample_type === "curd";

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="laboratorio-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900">Laboratorio</h1>
          <p className="text-sm text-stone-500 mt-1">Pruebas físico-químicas para leche cruda, crema y cuajada. Cada muestra se asocia al lote correspondiente.</p>
        </div>
        <Button onClick={() => setOpen(!open)} data-testid="new-lab-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
          <Plus className="w-4 h-4 mr-1.5" /> Nueva muestra
        </Button>
      </div>

      {open && (
        <Card className="p-6 border-stone-200 bg-white shadow-sm max-w-5xl">
          <form onSubmit={submit} className="space-y-5">
            {/* Tipo de muestra */}
            <div className="grid grid-cols-3 gap-2">
              {[["raw_milk", "Leche cruda", Milk], ["cream", "Crema", Factory], ["curd", "Cuajada", FlaskConical]].map(([v, l, Icon]) => (
                <button key={v} type="button" onClick={() => setForm({ ...empty, sample_type: v })}
                  data-testid={`type-${v}`}
                  className={`py-3 rounded-md border text-sm font-ibm-plex flex items-center justify-center gap-2 transition-colors ${
                    form.sample_type === v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-stone-700 border-stone-200 hover:bg-stone-50"
                  }`}>
                  <Icon className="w-4 h-4" /> {l}
                </button>
              ))}
            </div>

            {/* Lot selector */}
            {isMilk && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Lote MP *</Label>
                <Select value={form.reception_id} onValueChange={(v) => setForm({ ...form, reception_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="lab-reception"><SelectValue placeholder="Lote de leche cruda" /></SelectTrigger>
                  <SelectContent>{receptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.folio} · {r.supplier_name} · {r.liters} L</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {isCream && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Lote de crema (CRE-) *</Label>
                <Select value={form.cream_folio} onValueChange={(v) => setForm({ ...form, cream_folio: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="lab-cream"><SelectValue placeholder="Lote CRE-..." /></SelectTrigger>
                  <SelectContent>{productions.map((p) => <SelectItem key={p.id} value={p.cream_folio}>{p.cream_folio} · de {p.reception_folio} · {p.cream_kg} kg</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {isCurd && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Lote de cuajada (CUA-) *</Label>
                <Select value={form.curd_folio} onValueChange={(v) => setForm({ ...form, curd_folio: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="lab-curd"><SelectValue placeholder="Lote CUA-..." /></SelectTrigger>
                  <SelectContent>{productions.map((p) => <SelectItem key={p.id} value={p.curd_folio}>{p.curd_folio} · de {p.reception_folio} · {p.curd_kg} kg</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {/* Common: pH + acidez + temperatura */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <F label="pH" v={form.ph} on={(v) => setForm({ ...form, ph: v })} testid="lab-ph" />
              <F label={<>Acidez (°D) {phInfo && isMilk && <Badge>auto</Badge>}</>} v={form.acidity} on={(v) => setForm({ ...form, acidity: v })} placeholder={phInfo && isMilk ? `Auto: ${phInfo.acidity}` : ""} />
              <F label="Temperatura (°C)" v={form.temperature_c} on={(v) => setForm({ ...form, temperature_c: v })} />
              {isCurd ? <F label="Humedad (%)" v={form.humidity_pct} on={(v) => setForm({ ...form, humidity_pct: v })} /> : <F label="Grasa butírica (%)" v={form.fat_pct} on={(v) => setForm({ ...form, fat_pct: v })} />}
            </div>

            {/* Full physico-chemical block: raw_milk + cream */}
            {!isCurd && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <F label="Proteína (%)" v={form.protein_pct} on={(v) => setForm({ ...form, protein_pct: v })} />
                  <F label="Lactosa (%)" v={form.lactose_pct} on={(v) => setForm({ ...form, lactose_pct: v })} />
                  <F label="Densidad (g/mL)" v={form.density} on={(v) => setForm({ ...form, density: v })} step="0.001" />
                  <F label="Crioscópico (°H)" v={form.crioscopic_h} on={(v) => setForm({ ...form, crioscopic_h: v })} />
                  <F label="SNG (%)" v={form.sng_pct} on={(v) => setForm({ ...form, sng_pct: v })} />
                  <F label="Sólidos totales (%)" v={form.total_solids_pct} on={(v) => setForm({ ...form, total_solids_pct: v })} />
                  <SF label="Alcohol 75%" v={form.alcohol_test} on={(v) => setForm({ ...form, alcohol_test: v })} options={["", "Negativo", "Positivo"]} />
                  <SF label="Agentes neutralizantes" v={form.neutralizers} on={(v) => setForm({ ...form, neutralizers: v })} options={["", "Negativo", "Positivo"]} />
                  <SF label="Agente clorado" v={form.chlorinates} on={(v) => setForm({ ...form, chlorinates: v })} options={["", "Negativo", "Positivo"]} />
                  <SF label="Antibiótico" v={form.antibiotic} on={(v) => setForm({ ...form, antibiotic: v })} options={["", "Negativo", "Positivo"]} />
                  <F label="Color" v={form.color} on={(v) => setForm({ ...form, color: v })} type="text" placeholder="Blanco cremoso" />
                  <F label="Olor" v={form.smell} on={(v) => setForm({ ...form, smell: v })} type="text" placeholder="Característico puro" />
                  <F label="Sabor" v={form.taste} on={(v) => setForm({ ...form, taste: v })} type="text" placeholder="Característico puro" />
                  <F label="Aspecto" v={form.aspect} on={(v) => setForm({ ...form, aspect: v })} type="text" placeholder="Fluido sin grumos" />
                </div>
              </>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">URL Foto / evidencia</Label>
              <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="mt-1.5" placeholder="https://…" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" rows={2} />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="lab-submit-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Registrar muestra</Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border-stone-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full" data-testid="lab-table">
          <thead>
            <tr className="bg-stone-100 border-b border-stone-200">
              {["Fecha", "Tipo", "Lote", "pH", "Acidez", "Grasa/Humedad", "Responsable", "Acciones"].map((h) =>
                <th key={h} className="px-3 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-stone-500">Sin muestras.</td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-3 py-3 text-sm text-stone-700">{new Date(s.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}</td>
                <td className="px-3 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-outfit font-semibold ${TYPE_BADGE[s.sample_type || "raw_milk"]}`}>
                    {TYPE_LABEL[s.sample_type || "raw_milk"]}
                  </span>
                </td>
                <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A]">{s.reception_folio}</td>
                <td className="px-3 py-3 text-sm tabular-nums">{s.ph ?? "—"}</td>
                <td className="px-3 py-3 text-sm tabular-nums">{s.acidity ?? "—"}</td>
                <td className="px-3 py-3 text-sm tabular-nums">{s.fat_pct != null ? `${s.fat_pct}%` : s.humidity_pct != null ? `${s.humidity_pct}% H` : "—"}</td>
                <td className="px-3 py-3 text-sm text-stone-700">{s.responsible}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => navigate(`/trazabilidad?lote=${encodeURIComponent(s.reception_folio)}`)} title="Ver trazabilidad" className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-[#1E3A8A]/5"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => setToDelete(s)} data-testid={`delete-lab-${s.id}`} title="Eliminar" className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar muestra?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const F = ({ label, v, on, testid, placeholder, step = "0.01", type = "number" }) => (
  <div>
    <Label className="text-xs font-ibm-plex text-stone-700">{label}</Label>
    <Input type={type} step={type === "number" ? step : undefined} value={v} onChange={(e) => on(e.target.value)} className="mt-1.5" data-testid={testid} placeholder={placeholder} />
  </div>
);

const SF = ({ label, v, on, options }) => (
  <div>
    <Label className="text-xs font-ibm-plex text-stone-700">{label}</Label>
    <Select value={v} onValueChange={on}>
      <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>{options.filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const Badge = ({ children }) => (
  <span className="inline-block ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-[#1E3A8A]/10 text-[#1E3A8A] align-middle">{children}</span>
);
